import i18next from "i18next";

import { ATTACHMENT_LIMITS, MAX_VIDEO_FILE_SIZE, MB } from "@/config/constants";
import { formatFileSize } from "@/lib/utils";
import type { SubscriptionPlan } from "@/server/types/subscription";
import { attachmentKind, type AttachmentKind } from "./attachmentType";

/**
 * Attachment size limits: one place that decides which ceiling applies.
 *
 * The knobs themselves live in `config/constants.ts` (the scope × kind × plan
 * matrix); this module holds only the resolution rule, the transport clamp, and
 * the rejection copy. See docs/attachment-size-limits.md.
 */

type LimitInput = {
  /** MIME type, NOT a File — see the note on `getMaxAttachmentBytes`. */
  fileType?: string;
  /**
   * Whether the DESTINATION is an ORION Architect chat. Selects the scope row
   * of the matrix. Boolean because there are exactly two scopes today; if a
   * third ever appears, widen this to the `ChatScope` union and map at the
   * call sites (they already compute the Architect-ness).
   */
  isArchitect: boolean;
  plan: SubscriptionPlan;
};

/**
 * Video above the single-blob ceiling only ships via chunked upload, which is
 * flag-gated — and `MAX_VIDEO_FILE_SIZE` already encodes that flag (1000 MB on,
 * 100 MB off). So a video cell is clamped to what the transport can actually
 * carry right now: with the flag off, the 200/400/1000 tiers all collapse to
 * 100 MB. PDF and image are single-blob only; we keep their advertised caps and
 * track the backend-cap lift (100,000,000 bytes) as a separate action rather
 * than clamping here, so the advertised number stays honest once BE lands.
 */
const clampToTransport = (kind: AttachmentKind, cap: number): number =>
  kind === "video" ? Math.min(cap, MAX_VIDEO_FILE_SIZE) : cap;

/**
 * The maximum bytes allowed for this attachment in this chat, on this plan — or
 * `null` when the kind is not offered in this scope at all (today: video in an
 * Architect chat, which OA cannot process).
 *
 * The rule, in one line: look up `matrix[scope][kind][plan]`, fall back to the
 * FREE cell on an unknown plan, then clamp to the transport ceiling. An absent
 * `(scope, kind)` cell is a deliberate block, not a gap — see the matrix header
 * in constants.ts.
 *
 * Takes a MIME STRING and not a `File` deliberately. The places an attachment
 * can enter a chat know different things — staging and sending hold a `File`,
 * but forwarding only has the stored `attachmentMeta` — so a File-shaped
 * signature could not be called from all of them.
 *
 * The FREE fall-back is load-bearing, not defensive politeness: a bare
 * `table[plan]` yields `undefined`, and `size > undefined` is ALWAYS false — so
 * the cap would silently switch OFF instead of tightening. A new tier, a typo,
 * or a tampered persisted plan must fail closed.
 */
export function getMaxAttachmentBytes({
  fileType,
  isArchitect,
  plan,
}: LimitInput): number | null {
  const scope = isArchitect ? "architect" : "regular";
  const kind = attachmentKind(fileType);
  const table = ATTACHMENT_LIMITS[scope][kind];

  if (!table) {
    return null;
  }

  // `Object.hasOwn`, not `table[plan] ?? table.free`: a tampered plan naming an
  // Object.prototype member ("constructor", "toString") makes `table[plan]` the
  // inherited FUNCTION — not nullish, so `??` would let it through as a bogus
  // cap. hasOwn restricts the lookup to real cells and genuinely fails closed.
  const cap = Object.hasOwn(table, plan) ? table[plan] : table.free;
  return clampToTransport(kind, cap);
}

/**
 * The message to show if this attachment can't be sent, or `null` when it fits.
 *
 * Kept separate from `getMaxAttachmentBytes` so the two concerns — what the
 * limit is, and how a rejection reads — can move independently. Shared by every
 * check point so none of them can disagree about either.
 */
export function attachmentSizeRejection(
  fileSize: number,
  { fileType, isArchitect, plan }: LimitInput,
): string | null {
  // Reject empty files at the door. This is not tidiness: an empty file
  // encrypts to a valid 28-byte object (12-byte nonce + 16-byte tag), so it
  // uploads and sends normally, and decrypts back to 0 bytes — which
  // `decryptMessageAttachment` correctly treats as a failed decrypt and turns
  // into `null`. The result is an attachment that looks sent but is
  // permanently undownloadable for the sender and every recipient, with no
  // explanation. Nothing downstream can tell that case apart from a real
  // decrypt failure, so it has to be stopped here.
  if (fileSize === 0) {
    return i18next.t("emptyFileNotSupported");
  }

  const maxBytes = getMaxAttachmentBytes({ fileType, isArchitect, plan });

  if (maxBytes === null) {
    // The only unoffered cell today is video in an Architect chat, so the copy
    // is video-specific. Generalise this branch if another kind is ever blocked.
    return i18next.t("videoNotSupportedInArchitectChat");
  }

  if (fileSize <= maxBytes) {
    return null;
  }

  return isArchitect
    ? i18next.t("architectAttachmentSizeLimitExceeded", {
        fileSize: formatFileSize(fileSize, 1),
        limit: formatFileSize(maxBytes, 0),
      })
    : // Divide: the copy already appends "MB", so passing raw bytes read as
      // "limit of 20971520MB".
      i18next.t("fileSizeLimitExceeded", { size: maxBytes / MB });
}
